import { useMemo } from 'react'

import { useSelector } from 'react-redux'
import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import { faMapLocationDot } from '@fortawesome/pro-duotone-svg-icons'

import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import FormItem from '../../../../Components/shared/Forms/FormItem'
import IconButton from '../../../../Components/antd/Buttons/IconButton'
import AreasSelector from '../../../../Components/shared/Selectors/AreasSelector'
import { getIdOrNull, isNonEmptyArray } from '../../../../utils'
import { MAX_PLACES_PER_SUBMISSION } from '../../../../utils/entities/internshipRequest'
import { SchoolPortalRequestLine } from '../../../../utils/entities/schoolPortalRequest'
import PlacesNeedRow from './PlacesNeedRow'

export const TOTAL_PLACES_FIELD = 'totalPlaces'

const FIELD_STYLE = { width: '100%' }

const PlacesNeedsTable = ({
  areas = [], data = [], errorFields = [], hospitalDepartments = [], institution, institutionGroup, minDate, sections = [], sectionToYears = {},
  selectedArea = null, onAreaChange, onChange
}) => {
  const t = useSelector(selectTranslate)

  const totalPlaces = useMemo(() => data.reduce((total, line) => total + (line.total ?? 0), 0), [data])

  const isOverLimit = totalPlaces > MAX_PLACES_PER_SUBMISSION

  const handleAdd = () => onChange([...data, new SchoolPortalRequestLine({ institution: getIdOrNull(institutionGroup) === null ? institution : null })])
  const handleRemove = index => onChange(data.filter((line, i) => i !== index))
  const handleDuplicate = index => onChange([
    ...data.slice(0, index + 1),
    new SchoolPortalRequestLine({
      ...data[index],
      id: -1,
      requestPeriods: (data[index].requestPeriods ?? []).map(period => ({
        startDate: period.startDate?.clone() ?? null,
        endDate: period.endDate?.clone() ?? null
      }))
    }),
    ...data.slice(index + 1)
  ])
  const handleRowChange = index => line => onChange(data.map((current, i) => i === index ? line : current))

  const rowErrors = index => errorFields
    .filter(field => field.startsWith(`${index}.`))
    .map(field => field.slice(`${index}.`.length))

  return (
    <div className='pd-12'>
      {isNonEmptyArray(areas) && (
        <FormItem icon={faMapLocationDot} label='school_portal.requests.area_filter' help={t('school_portal.requests.area_filter_help')}>
          <AreasSelector
            areas={areas}
            placeholder={t('school_portal.requests.all_areas')}
            style={FIELD_STYLE}
            values={getIdOrNull(selectedArea)}
            onChange={onAreaChange}
          />
        </FormItem>
      )}

      {data.map((line, index) => (
        <PlacesNeedRow
          key={`line-${index}`}
          data={line}
          errorFields={rowErrors(index)}
          hospitalDepartments={hospitalDepartments}
          index={index}
          institution={institution}
          institutionGroup={institutionGroup}
          minDate={minDate}
          sections={sections}
          sectionToYears={sectionToYears}
          onChange={handleRowChange(index)}
          onDuplicate={handleDuplicate}
          onRemove={handleRemove}
        />
      ))}

      <IconButton
        className='mt-4'
        icon={faPlus}
        text={t('school_portal.requests.add_line')}
        onClick={handleAdd}
      />

      <div className={'mt-12 modal-inner-container' + (isOverLimit ? ' warning' : '')}>
        <b>{t('school_portal.requests.counter', { lines: data.length, places: totalPlaces })}</b>
        {isOverLimit && (
          <div className='input-error'>
            {t('school_portal.requests.places_limit_exceeded', { limit: MAX_PLACES_PER_SUBMISSION })}
          </div>
        )}
      </div>
    </div>
  )
}

export default PlacesNeedsTable
